/**
 * 일괄 작업 확인 다이얼로그/바텀시트
 * 선택된 사진에 대한 재분석, 삭제, 검토 완료 등의 작업 전 확인용
 */
import ConfirmActionModal, { type ConfirmVariant } from '../../ConfirmActionModal';
import type { IconComponent } from '../../icons';
import { ArrowPathIcon, CheckIcon, TrashIcon } from '../../icons';

// 일괄 작업 타입
export type BulkActionType = 'reanalyze' | 'delete' | 'review';

interface BulkActionConfig {
  title: string;
  description: (count: number) => string;
  warning?: string;
  confirmText: string;
  confirmVariant: ConfirmVariant;
  confirmStyle: string;
  /** 아이콘 컴포넌트(`components/icons`). 문자열 글리프는 받지 않는다. */
  icon: IconComponent;
}

const ACTION_CONFIGS: Record<BulkActionType, BulkActionConfig> = {
  reanalyze: {
    title: '재분석',
    description: (count) => `선택한 ${count}장의 사진을 재분석하시겠습니까?`,
    warning: '기존 분석 결과가 새로운 결과로 대체됩니다.',
    confirmText: '재분석',
    confirmVariant: 'primary',
    confirmStyle: 'bg-brand-600 hover:bg-brand-700 text-white',
    icon: ArrowPathIcon,
  },
  delete: {
    title: '삭제',
    description: (count) => `선택한 ${count}장의 사진을 삭제하시겠습니까?`,
    warning: '삭제된 사진과 해당 사진의 발견사항은 복구할 수 없습니다.',
    confirmText: '삭제',
    confirmVariant: 'danger',
    confirmStyle: 'bg-red-600 hover:bg-red-700 text-white',
    icon: TrashIcon,
  },
  review: {
    title: '검토 완료',
    description: (count) => `선택한 ${count}장의 사진을 검토 완료 처리하시겠습니까?`,
    confirmText: '검토 완료',
    confirmVariant: 'success',
    confirmStyle: 'bg-green-600 hover:bg-green-700 text-white',
    icon: CheckIcon,
  },
};

interface BulkActionConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  actionType: BulkActionType;
  selectedCount: number;
  isLoading?: boolean;
}

/**
 * Desktop/Fold용 일괄 작업 확인 다이얼로그
 */
export function BulkActionConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  actionType,
  selectedCount,
  isLoading = false,
}: BulkActionConfirmDialogProps) {
  const config = ACTION_CONFIGS[actionType];
  const ActionIcon = config.icon;

  const message = (
    <>
      <p className="mb-3">{config.description(selectedCount)}</p>
      {config.warning && (
        <div className="bg-amber-50 border border-amber-200 rounded-card p-3">
          <p className="text-sm text-amber-800">
            <span className="font-medium">주의:</span> {config.warning}
          </p>
        </div>
      )}
    </>
  );

  return (
    <ConfirmActionModal
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      title={config.title}
      icon={<ActionIcon className="h-6 w-6" />}
      message={message}
      confirmLabel={config.confirmText}
      confirmVariant={config.confirmVariant}
      loading={isLoading}
      size="sm"
    />
  );
}

interface BulkActionConfirmBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  actionType: BulkActionType;
  selectedCount: number;
  isLoading?: boolean;
}

/**
 * Phone용 일괄 작업 확인 바텀시트
 */
export function BulkActionConfirmBottomSheet({
  isOpen,
  onClose,
  onConfirm,
  actionType,
  selectedCount,
  isLoading = false,
}: BulkActionConfirmBottomSheetProps) {
  if (!isOpen) return null;

  const config = ACTION_CONFIGS[actionType];
  const ActionIcon = config.icon;

  return (
    // eslint-disable-next-line local/no-raw-overlay -- 바텀시트 갈래(base에 시트가 없다 — RAW_OVERLAY_EXEMPT docblock의 NOT_A_MODAL 판정)
    <div className="fixed inset-0 z-[70]">
      {/* 배경 오버레이 */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />

      {/* 바텀시트 */}
      <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl p-4 pb-8 animate-slide-up">
        {/* 핸들 */}
        <div className="w-10 h-1 bg-ink-300 rounded-full mx-auto mb-4" />

        {/* 아이콘 + 제목 */}
        <div className="flex items-center gap-3 mb-3">
          <ActionIcon className="h-5 w-5 text-ink-500" />
          <h3 className="text-lg font-semibold text-ink-900">{config.title}</h3>
        </div>

        {/* 설명 */}
        <p className="text-ink-600 mb-3 text-sm">
          {config.description(selectedCount)}
        </p>

        {/* 경고 메시지 */}
        {config.warning && (
          <div className="bg-amber-50 border border-amber-200 rounded-card p-3 mb-4">
            <p className="text-sm text-amber-800">
              <span className="font-medium">주의:</span> {config.warning}
            </p>
          </div>
        )}

        {/* 버튼 */}
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className={`w-full py-3 text-sm font-medium rounded-card-sm disabled:opacity-50 ${config.confirmStyle}`}
          >
            {isLoading ? '처리 중...' : config.confirmText}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="w-full py-3 text-sm font-medium text-ink-700 bg-surface-hover rounded-card-sm hover:bg-ink-200 disabled:opacity-50"
          >
            취소
          </button>
        </div>
      </div>
    </div>
  );
}

export default BulkActionConfirmDialog;
